import React, { useState, useEffect } from 'react';
import { Chips, TableMenu } from '@gerdau/hefesto-mais-web';
import { Button, Icon, Title } from '@gerdau/hefesto';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { IFabricationProjectsState } from '../../../../../store/ducks/fabrication-projects/types';
import {
  getDefaultFabricationProjectItem,
  IDetailingProjectState,
  IFabricationProjectItem
} from '../../../../../store/ducks/fabrication-purchase-order/types';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import { DetailingProjectBlock } from '../styles';
import DetailingProjectTable from './DetailingProjectItem';
import DetailingProjectUploadFiles from './DetailingProjectUploadFiles';
import { Subtitle } from './styles';
import { makeid } from '../../../../../utils/formatStrings';
import { TChipArray } from '@gerdau/hefesto-mais-web/Chips/Chips.types';

export enum EUploadProjectOption {
  MOCKUP_PROJECT = '1',
  FREE_PROJECT = '2'
}
interface IDetailingProjectContainerProps {
  contractNumber: string;
  detailingProjectState: IDetailingProjectState;
  setDetailingProjectState: React.Dispatch<
    React.SetStateAction<IDetailingProjectState>
  >;
  fabricationProjectsState: IFabricationProjectsState;
}

const DetailingProjectContainerFacelift: React.FC<
  IDetailingProjectContainerProps
> = ({
  contractNumber,
  detailingProjectState,
  setDetailingProjectState,
  fabricationProjectsState
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { screenName, itemDescription } = getTaggingInformationScreen(
    history.location.pathname,
    true
  );

  // Ao voltar para esta etapa os projetos já estão carregados: a opção
  // inicial precisa refletir isso, pois trocar a seleção enquanto o ChipSet
  // inicializa gera um loop infinito de atualizações.
  const getInitialUploadProjectOption = (): EUploadProjectOption =>
    !fabricationProjectsState.loading &&
    fabricationProjectsState.contractNumber === contractNumber &&
    fabricationProjectsState.projects?.length === 0
      ? EUploadProjectOption.FREE_PROJECT
      : EUploadProjectOption.MOCKUP_PROJECT;

  const [selectedUploadProjectOption, setSelectedUploadProjectOption] =
    useState<EUploadProjectOption>(getInitialUploadProjectOption);

  const uploadProjectOptions: TChipArray[] = [
    {
      id: EUploadProjectOption.MOCKUP_PROJECT,
      label: 'Utilizar arquivos de projetos cadastrados'
    },
    {
      id: EUploadProjectOption.FREE_PROJECT,
      label: 'Enviar projetos somente para este pedido'
    }
  ];

  const onChangeOptionUploadProjectOption = (
    newOption: EUploadProjectOption
  ) => {
    setSelectedUploadProjectOption(newOption);

    const removeItemsWithoutProjectId = () => {
      const itens = detailingProjectState.itens.filter(
        (item) => item.projectId
      );
      setDetailingProjectState((prev) => ({
        ...prev,
        itens: itens.length > 0 ? itens : [getDefaultFabricationProjectItem()]
      }));
    };
    removeItemsWithoutProjectId();
  };

  const onClickAddItem = () => {
    setDetailingProjectState((prev) => ({
      ...prev,
      itens: [
        ...detailingProjectState.itens,
        getDefaultFabricationProjectItem()
      ]
    }));

    dispatch(
      sendTagging({
        screenName,
        itemDescription: `${itemDescription}_adicionarProjeto`,
        itemName: 'button:adicionar_projeto',
        event: 'click'
      })
    );
  };

  const onRemoveItemClick = (key: string) => {
    if (detailingProjectState.itens.length > 1) {
      const updatedItens = [...detailingProjectState.itens];
      const itemToRemoveIdx = updatedItens.findIndex((ui) => ui.key === key);
      updatedItens.splice(itemToRemoveIdx, 1);
      setDetailingProjectState((prev) => ({
        ...prev,
        itens: updatedItens
      }));
    } else {
      setDetailingProjectState((prev) => ({
        ...prev,
        itens: [getDefaultFabricationProjectItem()]
      }));
    }
  };

  const onUpdateFabricationItem = (item: IFabricationProjectItem) => {
    const itensAux = [...detailingProjectState.itens];

    const updatedItemIdx = detailingProjectState.itens.findIndex(
      (it) => it.key === item.key
    );

    itensAux[updatedItemIdx] = item;
    setDetailingProjectState((prev) => ({ ...prev, itens: itensAux }));
  };

  const [afterProjectsLoadChecked, setAfterProjectsLoadChecked] =
    useState(false);

  const checkIfHasAnyProjectWithMockupId = () => {
    setAfterProjectsLoadChecked(true);
    if (
      fabricationProjectsState.projects &&
      fabricationProjectsState.projects.length === 0
    ) {
      setSelectedUploadProjectOption(EUploadProjectOption.FREE_PROJECT);
    }
  };

  const initSelectedProjectIfNeed = () => {
    const itens = JSON.parse(
      JSON.stringify(detailingProjectState.itens)
    ) as IFabricationProjectItem[];

    itens.map((item) => {
      if (
        item.projectId &&
        (!item.projectSelected || item.projectSelected.id !== item.projectId)
      ) {
        const projectSelected = fabricationProjectsState.projects?.find(
          (p) => p.id === item.projectId
        );
        item.projectSelected = projectSelected;
        item.key = makeid(6);
        item.revision = projectSelected?.revision || '';
      }
    });
    setDetailingProjectState((prev) => ({ ...prev, itens }));
  };

  useEffect(() => {
    if (
      !afterProjectsLoadChecked &&
      !fabricationProjectsState.loading &&
      !fabricationProjectsState.error &&
      fabricationProjectsState.contractNumber === contractNumber
    ) {
      setAfterProjectsLoadChecked(true);
      checkIfHasAnyProjectWithMockupId();
      initSelectedProjectIfNeed();
    }
  });

  return (
    <>
      <DetailingProjectBlock>
        <Title level={2}>Arquivos do projeto</Title>
        <Subtitle>Como deseja enviar os arquivos?</Subtitle>

        <Chips
          type="choice"
          chips={uploadProjectOptions}
          selectedChipIds={[selectedUploadProjectOption]}
          handleSelect={(option) => {
            if (option.length && option[0] !== selectedUploadProjectOption) {
              onChangeOptionUploadProjectOption(
                option[0] as EUploadProjectOption
              );
            }
          }}
        />

        {selectedUploadProjectOption === EUploadProjectOption.FREE_PROJECT && (
          <DetailingProjectUploadFiles
            contractNumber={contractNumber}
            detailingProjectState={detailingProjectState}
            setDetailingProjectState={setDetailingProjectState}
          />
        )}
      </DetailingProjectBlock>

      {selectedUploadProjectOption === EUploadProjectOption.MOCKUP_PROJECT && (
        <TableMenu
          rightComponents={
            <Button
              name="addProjectButton"
              variant="secondary"
              icon={<Icon option="Plus" />}
              onClick={onClickAddItem}
            >
              Adicionar projeto
            </Button>
          }
        />
      )}

      <DetailingProjectBlock>
        <DetailingProjectTable
          fabricationProjectsState={fabricationProjectsState}
          items={detailingProjectState.itens}
          updateItem={onUpdateFabricationItem}
          onRemoveItem={onRemoveItemClick}
          isTheOnlyItem={detailingProjectState.itens.length === 1}
          uploadProjectOption={selectedUploadProjectOption}
        />
      </DetailingProjectBlock>
    </>
  );
};

export default DetailingProjectContainerFacelift;
